<template>
  <v-dialog
    :value="value" @input="$emit('input')"
    width="990"
  >
    <v-card>
      <v-card-title class="text-h5">
        <template v-if="item.paysystem_id">Update Paysystem #{{ item.paysystem_id }}</template>
        <template v-else>Create Paysystem</template>
      </v-card-title>

      <v-card-text>

        <v-text-field
          class="mb-2"
          label="Name"
          v-model="item.name"
          hide-details
          :loading="loading"
          :disabled="loading"
          required
        />

        <v-text-field
          class="mb-2"
          label="Internal Name"
          v-model="item.internal_name"
          hide-details
          :loading="loading"
          :disabled="loading"
          required
        />

        <v-text-field
          class="mb-2"
          label="Comment"
          v-model="item.comments"
          :loading="loading"
          :disabled="loading"
          hide-details
          required
        />

        <v-autocomplete
          class="mb-2"
          label="Settings Template"
          :loading="loading || loadingSettings"
          :disabled="loading || loadingSettings"
          v-model="template"
          :items="templates"
          clearable
          auto-select-first
          item-text="alias"
          item-value="paysystem"
          hide-details
          :return-object="true"
          append-outer-icon="mdi-arrow-down-bold-outline"
          @click:append-outer="applyTemplate"
        />


        <v-label>Settings</v-label>

        <vue-json-editor
          :loading="loading"
          :disabled="loading"
          v-model="item.settings"
          mode="code"
          :modes="[]"
          :show-btns="false"
          :exapndedOnStart="true"
        />
        <non-ascii-alert :source-object="item.settings" />

        <fieldset v-if="template" class="px-2 pb-2 group-border">
          <legend class="px-2">Template Legend</legend>

          <v-list-item
            v-for="item of template.paysystem"
            dense
            class="settings-legend"
            :key="item.key"
          >
            <v-list-item-icon>
              <v-icon>mdi-circle-small</v-icon>
            </v-list-item-icon>
            <v-list-item-content class="py-1">
              <v-list-item-title>
                {{ item.key }} ({{ item.type }})
                <template v-if="item.default">, Default: {{ item.default }}</template>
              </v-list-item-title>
              <v-list-item-subtitle v-if="item.comment">
                {{ item.comment }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </fieldset>

        <template v-if="item.paysystem_id">

          <v-divider class="my-5" />

          <v-label>Payment Methods</v-label>

          <v-progress-linear
            v-if="loadingPaymethod"
            indeterminate
            color="green"
          />


          <v-expansion-panels
            v-model="expansioonPanels"
            multiple
            hover
            class="paysystem-settings mt-4"
          >
            <template v-for="(items, entity_id) in paymethods">

              <v-expansion-panel
                v-if="items != null"
              >
                <v-expansion-panel-header class="paysystem-settings_header">
                  <div style="line-height: 28px">
                    {{ (projects[entity_id] || {}).text }} #{{ entity_id }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table dense>
                    <template>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Title</th>
                          <th>Whitelist</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="paymethod in items">
                          <td>{{ paymethod.name }}</td>
                          <td>{{ paymethod.code }}</td>
                          <td>{{ paymethod.title }}</td>
                          <td>
                            <span class="text-no-wrap" v-if="paymethod.settings.whitelist">
                              AffiliateTag: <b>{{ paymethod.settings.whitelist.affiliate_tag }}</b><br />
                              Dep. Count: <b>{{ paymethod.settings.whitelist.deposit_count }}</b><br />
                              Dep. Amount: <b>{{ paymethod.settings.whitelist.deposit_sum }} EUR</b>
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>

            </template>
          </v-expansion-panels>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn
          color="warning"
          text
          :loading="loading"
          :disabled="loading"
          @click.native="$emit('input')"
        >
          Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="info"
          text
          :loading="loading"
          :disabled="loading"
          @click="saveAs(item)"
        >
          Save as
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          :loading="loading"
          :disabled="loading"
          @click="save(item)"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style>
  .settings-legend.v-list-item {
    min-height: 20px;
  }

  .jsoneditor-menu {
    display: none !important;
  }

  div.jsoneditor-mode-code {
    height: 300px;
  }

  div.jsoneditor-outer {
    padding-top: 0px;
    margin-top: 0px;
  }

  .paysystem-settings .paysystem-settings_header {
    min-height: 34px;
    padding: 0px 10px;
  }

  .paysystem-settings .paysystem-settings_header .v-chip {
    margin-top: 6px;
  }
</style>

<script>
  import vueJsonEditor from 'vue-json-editor'

  import { API } from '@/utils/api'
  import { omit, find, isEmpty, groupBy, keyBy, map } from 'lodash'
  import NonAsciiAlert from '../NonAsciiAlert.vue'

  export default {
    props: {
      projectList: { default: () => ([]) },
      paysystems: { default: () => ([]) },
      value: { default: false },
      refresh: { default: false },
      item: { default: () => ({}) },
    },

    components: {
      NonAsciiAlert,
      vueJsonEditor,
    },

    data: () => ({
      expansioonPanels: [],
      loadingSettings: false,
      loading: false,
      loadingPaymethod: false,
      template: null,
      templates: [],
      paymethods: [],
      headers: [
        { text: 'Key', value: 'key', sortable: false },
        { text: 'Value', value: 'value', sortable: false },
      ],
    }),

    watch: {
      value () {
        if (this.value) {
          this.paymethods = []
          this.loadPaymentSettings()
          if (this.item.paysystem_id) {
            this.loadAssociatedPaymethods()
          }
          this.template = null
        }
      },

      template () {
        if (isEmpty(this.item.settings)) this.applyTemplate()
      },
      'item.name' () {
        let template = find(this.templates, { alias: this.item.name })
        if (template) {
          this.template = template
        }
      },
    },

    computed: {
      projects () {
        return (keyBy(map(this.$store.state.auth.entities, (i) => ({ text: i.name, value: i.entity_id })), 'value'))
      },
    },

    methods: {
      applyTemplate () {
        this.item.settings = {}

        if (!this.template) return

        let defaultValues = {
          string: '',
          number: 0,
          bool: false,
          default: null
        }

        for (let item of this.template.paysystem) {
          let value = defaultValues.default
          if (item.hasOwnProperty('default')) {
            value = item.default
          } else if (defaultValues.hasOwnProperty(item.type)) {
            value = defaultValues[item.type]
          }

          this.item.settings[item.key] = value
        }
      },

      loadPaymentSettings () {
        this.loadingSettings = true
        return API.call('/settings/payment/settings.json', {}).then(resp => {
          this.templates = resp.data

          this.template = find(this.templates, { alias: this.item.name })
        }).finally(() => {
          this.loadingSettings = false
        })
      },
      async loadAssociatedPaymethods () {
        this.paymethods = []
        this.loadingPaymethod = true
        return API.call('/$v/paymethod/list.json', { paysystem_id: this.item.paysystem_id, all: true }, null, this.$store.state.auth.entities[0]).then(result => {
          this.paymethods = groupBy(result.data, 'entity_id')
        }).finally(() => {
          this.loadingPaymethod = false
        })
      },
      async save (item, message = 'Save paysystem', saveAndClose = true) {
        let buttons = []
        if (!saveAndClose) {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        } else {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Save and close', value: 'close', class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        }

        let confirmRes = await this.$root.$confirm('Confirm', message, { width: 515, buttons })

        if (!confirmRes) {
          return
        }

        this.loading = true
        return API.call('/$v/paysystem/update.json', { items: [ item ] }).then(resp => {

          this.$notification.show = true
          this.$notification.message = 'Paysystems updated'
          this.$notification.type = 'success'

          return this.refresh()
        }).finally(() => {
          this.loading = false

          if (confirmRes == 'close') {
            this.$emit('input')
          }
        })
      },
      async saveAs (item) {
        return this.save(omit(item, 'paysystem_id'), 'Save paysystem as new', false)
      }
    }
  }
</script>
